import React, { useContext, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Select,
  InputNumber,
  message,
  Divider,
  Alert,
  Row,
  Col,
  Typography,
  Space,
  Collapse,
  Tag,
} from "antd";
import {
  BellOutlined,
  MailOutlined,
  MessageOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import {
  getOrCreateNotificationSettings,
  updateNotificationSettings,
  updateEmailSettings,
  updateLateFeeSettings,
  testEmailConfiguration,
} from "../lib/firebase/notificationSettingsModel";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const NotificationSettings = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form] = Form.useForm();

  const loadSettings = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const notificationSettings = await getOrCreateNotificationSettings();
      setSettings(notificationSettings);
      form.setFieldsValue(notificationSettings);
    } catch (error) {
      console.error("Error loading notification settings:", error);
      message.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }, [user, form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveSettings = async (values) => {
    try {
      setSaving(true);

      // Update notification settings
      await updateNotificationSettings({
        defaultRentReminders: values.defaultRentReminders,
        defaultLateNotifications: values.defaultLateNotifications,
        emailSettings: values.emailSettings,
        smsSettings: values.smsSettings,
        lateFeeSettings: values.lateFeeSettings,
      });

      message.success("Notification settings saved successfully!");
      await loadSettings(); // Reload to get updated data
    } catch (error) {
      console.error("Error saving notification settings:", error);
      message.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      await testEmailConfiguration();
      message.success("Email test completed successfully!");
    } catch (error) {
      console.error("Error testing email:", error);
      message.error("Email test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <div style={{ fontSize: "1.2rem", color: "var(--color-text-light)" }}>
            Loading notification settings...
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="notification-settings-container">
      <Header>
        <HeaderContent>
          <div className="header-text-container">
            <Title>
              <BellOutlined style={{ marginRight: "12px", fontSize: "2rem" }} />
              Notification Settings
            </Title>
            <Subtitle>
              Configure how and when tenants receive rent reminders and late
              notices
            </Subtitle>
          </div>
        </HeaderContent>
      </Header>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveSettings}
        initialValues={settings}
      >
        {/* Default Settings */}
        <SettingsCard
          title="Default Notification Settings"
          className="section-card"
        >
          <Alert
            message="Default Settings"
            description="These settings will be applied to new tenants. Existing tenants can have their own custom settings."
            type="info"
            showIcon
            style={{ marginBottom: "1.5rem" }}
          />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Form.List name="defaultRentReminders">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ marginBottom: "1rem" }}>
                      <Text strong>Default Rent Reminders</Text>
                      <Text type="secondary" style={{ marginLeft: "0.5rem" }}>
                        (Applied to new tenants)
                      </Text>
                    </div>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row
                        gutter={16}
                        key={key}
                        style={{ marginBottom: "1rem" }}
                      >
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "daysBeforeDue"]}
                            label="Days Before Due"
                            rules={[
                              { required: true, message: "Required" },
                              {
                                type: "number",
                                min: 1,
                                max: 30,
                                message: "1-30 days",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="Days"
                              min={1}
                              max={30}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "channel"]}
                            label="Channel"
                            rules={[{ required: true, message: "Required" }]}
                          >
                            <Select style={{ width: "100%" }}>
                              <Option value="email">Email</Option>
                              <Option value="sms">SMS</Option>
                              <Option value="both">Both</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "enabled"]}
                            label="Enabled"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: "32px" }}
                            danger
                          />
                        </Col>
                      </Row>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            daysBeforeDue: 7,
                            enabled: true,
                            channel: "email",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Default Reminder
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Col>

            <Col xs={24} lg={12}>
              <Form.List name="defaultLateNotifications">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ marginBottom: "1rem" }}>
                      <Text strong>Default Late Notifications</Text>
                      <Text type="secondary" style={{ marginLeft: "0.5rem" }}>
                        (Applied to new tenants)
                      </Text>
                    </div>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row
                        gutter={16}
                        key={key}
                        style={{ marginBottom: "1rem" }}
                      >
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "daysAfterDue"]}
                            label="Days After Due"
                            rules={[
                              { required: true, message: "Required" },
                              {
                                type: "number",
                                min: 1,
                                max: 30,
                                message: "1-30 days",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="Days"
                              min={1}
                              max={30}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "channel"]}
                            label="Channel"
                            rules={[{ required: true, message: "Required" }]}
                          >
                            <Select style={{ width: "100%" }}>
                              <Option value="email">Email</Option>
                              <Option value="sms">SMS</Option>
                              <Option value="both">Both</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "enabled"]}
                            label="Enabled"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: "32px" }}
                            danger
                          />
                        </Col>
                      </Row>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            daysAfterDue: 1,
                            enabled: true,
                            channel: "email",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Default Late Notification
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Col>
          </Row>
        </SettingsCard>

        {/* Email Settings */}
        <SettingsCard title="Email Configuration" className="section-card">
          <Collapse
            ghost
            items={[
              {
                key: "emailSettings",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <MailOutlined style={{ color: "var(--color-primary)" }} />
                    <span
                      style={{
                        fontFamily: "var(--font-secondary)",
                        fontWeight: 600,
                      }}
                    >
                      SMTP Settings
                    </span>
                  </div>
                ),
                children: (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "fromEmail"]}
                        label="From Email"
                        rules={[
                          {
                            required: true,
                            message: "Please enter from email",
                          },
                          {
                            type: "email",
                            message: "Please enter a valid email",
                          },
                        ]}
                      >
                        <Input placeholder="noreply@clumse.com" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "fromName"]}
                        label="From Name"
                        rules={[
                          { required: true, message: "Please enter from name" },
                        ]}
                      >
                        <Input placeholder="Clumse" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "smtpSettings", "host"]}
                        label="SMTP Host"
                        rules={[
                          { required: true, message: "Please enter SMTP host" },
                        ]}
                      >
                        <Input placeholder="smtp.gmail.com" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "smtpSettings", "port"]}
                        label="SMTP Port"
                        rules={[
                          { required: true, message: "Please enter SMTP port" },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="587"
                          min={1}
                          max={65535}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "smtpSettings", "username"]}
                        label="SMTP Username"
                        rules={[
                          {
                            required: true,
                            message: "Please enter SMTP username",
                          },
                        ]}
                      >
                        <Input placeholder="your-email@gmail.com" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={["emailSettings", "smtpSettings", "password"]}
                        label="SMTP Password"
                        rules={[
                          {
                            required: true,
                            message: "Please enter SMTP password",
                          },
                        ]}
                      >
                        <Input.Password placeholder="Your app password" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Space>
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={handleTestEmail}
                          loading={testing}
                        >
                          Test Email Configuration
                        </Button>
                        <Text type="secondary">
                          Test your email settings before saving
                        </Text>
                      </Space>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "lateFeeSettings",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ExclamationCircleOutlined
                      style={{ color: "var(--color-primary)" }}
                    />
                    <span
                      style={{
                        fontFamily: "var(--font-secondary)",
                        fontWeight: 600,
                      }}
                    >
                      Late Fee Configuration
                    </span>
                  </div>
                ),
                children: (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name={["lateFeeSettings", "amount"]}
                        label="Late Fee Amount"
                        rules={[
                          {
                            required: true,
                            message: "Please enter late fee amount",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="50.00"
                          min={0}
                          precision={2}
                          prefix="$"
                        />
                      </Form.Item>
                      <Form.Item
                        name={["lateFeeSettings", "gracePeriod"]}
                        label="Grace Period (Days)"
                        rules={[
                          {
                            required: true,
                            message: "Please enter grace period",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="3"
                          min={0}
                          max={30}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name={["lateFeeSettings", "accruesDaily"]}
                        label="Accrues Daily"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name={["lateFeeSettings", "dailyAmount"]}
                        label="Daily Late Fee Amount"
                        rules={[
                          {
                            required: true,
                            message: "Please enter daily late fee amount",
                            validator: (_, value) => {
                              const accruesDaily = form.getFieldValue([
                                "lateFeeSettings",
                                "accruesDaily",
                              ]);
                              if (accruesDaily && !value) {
                                return Promise.reject(
                                  "Required when accrues daily is enabled"
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="10.00"
                          min={0}
                          precision={2}
                          prefix="$"
                          disabled={
                            !form.getFieldValue([
                              "lateFeeSettings",
                              "accruesDaily",
                            ])
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Alert
                        message="Late Fee Policy"
                        description="Late fees are automatically applied based on your settings. Email templates are managed by the backend system."
                        type="info"
                        showIcon
                        style={{ marginBottom: "1rem" }}
                      />
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        </SettingsCard>

        {/* Save Button */}
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={saving}
            icon={<SettingOutlined />}
            style={{
              height: "48px",
              padding: "0 2rem",
              fontSize: "1.1rem",
              fontWeight: 600,
            }}
          >
            Save Notification Settings
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default NotificationSettings;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 1200px) {
    padding: 1.5rem;
  }

  &.notification-settings-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  }
`;

const Header = styled.div`
  background: var(--gradient-primary);
  border-radius: 20px;
  padding: 1.25rem 2rem;
  margin-bottom: 1.5rem;
  color: white;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-text-container {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  margin: 0;
  opacity: 0.95;
  color: white;
  font-weight: 500;
  letter-spacing: -0.01em;
  position: relative;
  z-index: 1;
  margin-top: 0.75rem;
`;

const SettingsCard = styled(Card)`
  border-radius: 20px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  background: white;
  margin-bottom: 2rem;
  overflow: hidden;

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
  }

  .ant-card-head-title {
    padding: 1.5rem 2rem 1rem 2rem;
    font-family: var(--font-primary);
    font-size: 1.5rem;
    font-weight: 700;
    color: white !important;
  }

  .ant-card-body {
    padding: 2rem;
  }

  &.section-card {
    background: #ffffff !important;
    border: 2px solid #e2e8f0 !important;
    border-radius: 20px !important;
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08) !important;
    margin-bottom: 2rem !important;
    overflow: hidden !important;
  }
`;
